import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Token-Auth (Bearer) ───────────────────────────────────────
// Auth laeuft ueber einen JWT im `Authorization: Bearer`-Header, NICHT
// mehr ueber ein httpOnly-Cookie. Vorteil: Cross-Site-/iframe-tauglich
// (kein Third-Party-Cookie, das Safari/Chrome blocken) und kein CSRF
// noetig — das Backend verlangt den CSRF-Header nur, wenn ein Auth-
// Cookie mitkommt. Persistenz via localStorage, damit die Session
// einen Reload ueberlebt. (Embed-Build kann den Token spaeter per
// postMessage vom Parent bekommen statt aus localStorage.)
const TOKEN_KEY = 'predfuture.access_token';
let _accessToken = null;
try {
  _accessToken = window.localStorage.getItem(TOKEN_KEY);
} catch { /* localStorage gesperrt */ }

export const setAccessToken = (token) => {
  _accessToken = token || null;
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
};
export const getAccessToken = () => _accessToken;
export const clearAccessToken = () => setAccessToken(null);

const instance = axios.create({
  baseURL: `${API_URL}/api`,
});

// Bei jedem Request den Bearer-Token mitschicken (falls vorhanden).
instance.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${_accessToken}` };
  }
  return config;
});

// 401 interceptor — Token fehlt/abgelaufen/revoked -> Session verwerfen.
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAccessToken();
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────
// Token-Verwaltung zentral hier, damit kein Caller sie vergessen kann.
// login/register/refresh/change-password liefern einen frischen Token
// im Body -> speichern. logout -> verwerfen.
const auth = {
  login: async (data) => {
    const res = await instance.post('/auth/login', data);
    if (res.data?.access_token) setAccessToken(res.data.access_token);
    return res;
  },
  register: async (data) => {
    const res = await instance.post('/auth/register', data);
    if (res.data?.access_token) setAccessToken(res.data.access_token);
    return res;
  },
  me: () => instance.get('/auth/me'),
  logout: async () => {
    try {
      return await instance.post('/auth/logout');
    } finally {
      clearAccessToken();
    }
  },
  refresh: async () => {
    const res = await instance.post('/auth/refresh');
    if (res.data?.access_token) setAccessToken(res.data.access_token);
    return res;
  },
  changePassword: async (data) => {
    const res = await instance.post('/auth/change-password', data);
    // Frischer Token nach Passwortwechsel (alte sind serverseitig
    // ungueltig) -> gespeicherten Token ersetzen, sonst sofort 401.
    if (res.data?.access_token) setAccessToken(res.data.access_token);
    return res;
  },
  wsToken: () => instance.get('/auth/ws-token'),
};

// ── Markets ───────────────────────────────────────────────────
const markets = {
  getAll: (params) => instance.get('/markets', { params }),
  get: (id) => instance.get(`/markets/${id}`),
  create: (data) => instance.post('/markets', data),
  getOdds: (id) => instance.get(`/markets/${id}/odds`),
  getOutcomes: (id) => instance.get(`/markets/${id}/outcomes`),
  getCandles: (id, params) => instance.get(`/markets/${id}/candles`, { params }),
  getPriceHistory: (id, params) => instance.get(`/markets/${id}/price-history`, { params }),
  trending: () => instance.get('/markets/trending'),
  search: (params) => instance.get('/markets/search', { params }),
  categories: () => instance.get('/markets/categories'),
};

// ── Civic ─────────────────────────────────────────────────────
const civic = {
  getMarkets: (params) => instance.get('/civic/markets', { params }),
  getMarket: (id) => instance.get(`/civic/markets/${id}`),
  vote: (marketId, direction) => instance.post('/civic/vote', { market_id: marketId, direction }),
  getResults: (marketId) => instance.get(`/civic/results/${marketId}`),
  // Bulk-Tally aller Civic-Markets in einem Call (vermeidet N+1 auf den
  // Listen-Seiten). Rueckgabe: { market_id: result }.
  getResultsBulk: () => instance.get('/civic/results/bulk'),
  // Daily yes/no tally for the civic vote-history chart.
  getVoteHistory: (marketId, params) => instance.get(`/civic/markets/${marketId}/vote-history`, { params }),
  // Current user's own civic-vote history — surfaced alongside bets
  // in profile + portfolio so civic activity shows up in the same
  // places prediction bets do.
  getMyVotes: () => instance.get('/civic/votes/my'),
  createProposal: (data) => instance.post('/civic/proposals', data),
  getVotingStats: () => instance.get('/civic/voting-stats'),
};

// ── Bets ──────────────────────────────────────────────────────
const bets = {
  // idempotencyKey macht Doppel-Submits (Doppelklick, Netzwerk-Retry)
  // sicher: das Backend leitet daraus den CreditTransaction-ref ab und
  // weist einen zweiten Request mit gleichem Key per 409 ab.
  place: (data, idempotencyKey) =>
    instance.post('/bets', data, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),
  quote: (data) => instance.post('/bets/quote', data),
};

// ── Outcomes ──────────────────────────────────────────────────
const outcomes = {
  getBulk: (params) => instance.get('/outcomes/bulk', { params }),
};

// ── Credits ───────────────────────────────────────────────────
const credits = {
  getBalance: (userId) => instance.get(`/credits/${userId}`),
  getPackages: () => instance.get('/credit-packages'),
  purchase: (data) => instance.post('/purchase-credits', data),
};

// ── Profile ───────────────────────────────────────────────────
const profile = {
  getMe: () => instance.get('/profile/me'),
  update: (data) => instance.put('/profile/me', data),
  getByHandle: (handle) => instance.get(`/profile/${encodeURIComponent(handle)}`),
  getBettingHistory: (params) => instance.get('/profile/betting-history', { params }),
  getEarnings: () => instance.get('/profile/earnings'),
  getTransactionHistory: (params) => instance.get('/profile/transaction-history', { params }),
  getCommissions: () => instance.get('/user/commissions'),
  searchUsers: (q) => instance.get(`/users/search?q=${encodeURIComponent(q)}`),
  updateHandle: (handle) => instance.put(`/profile/update-handle?new_handle=${encodeURIComponent(handle)}`),
  updateLeaderboardVisibility: (visible) => instance.put(`/profile/leaderboard-visibility?visible=${visible}`),
  updatePrivacy: (key, value) => instance.put(`/profile/privacy?${key}=${value}`),
  getDailyBonusStatus: () => instance.get('/profile/daily-bonus/status'),
  claimDailyBonus: () => instance.post('/profile/daily-bonus/claim'),
  deleteAccount: () => instance.delete('/profile/account'),
};

// ── Social / Friends ──────────────────────────────────────────
const friends = {
  list: () => instance.get('/friends'),
  request: (data) => instance.post('/friends/request', data),
  requestByHandle: (handle) => instance.post(`/friends/request?addressee_handle=${encodeURIComponent(handle)}`),
  respond: (data) => instance.post('/friends/respond', data),
  accept: (id) => instance.put(`/friends/${id}/accept`),
  reject: (id) => instance.put(`/friends/${id}/reject`),
  remove: (id) => instance.delete(`/friends/${id}`),
  getRequests: () => instance.get('/friends/requests'),
  leaderboard: () => instance.get('/friends/leaderboard'),
  suggestions: () => instance.get('/friends/suggestions'),
  getActivity: (marketId) => instance.get(`/friends/activity/${marketId}`),
  // Bulk replacement for getActivity — returns { market_id: count }
  // for every market the current user's opted-in friends have bet on.
  getActivityBulk: () => instance.get('/friends/activity'),
};

// ── Notifications ─────────────────────────────────────────────
const notifications = {
  // Match the actual backend routes (server.py):
  //   GET /notifications/recent  -> last 10 (header dropdown)
  //   GET /notifications/all     -> full list (profile page)
  //   GET /notifications/count   -> { count: <unread> } (badge)
  //   PUT /notifications/{id}/read
  //   PUT /notifications/mark-all-read
  // The previous `/notifications` and `/notifications/unread-count`
  // paths returned 404 — that was the empty-profile-bell bug.
  getRecent: () => instance.get('/notifications/recent'),
  getAll: () => instance.get('/notifications/all'),
  getUnreadCount: () => instance.get('/notifications/count'),
  markRead: (id) => instance.put(`/notifications/${id}/read`),
  markAllRead: () => instance.put('/notifications/mark-all-read'),
};

// ── Leaderboard ───────────────────────────────────────────────
const leaderboard = {
  get: (params) => instance.get('/leaderboard', { params }),
};

// ── Referrals ─────────────────────────────────────────────────
const referrals = {
  getStats: () => instance.get('/referrals/stats'),
  getUserStats: () => instance.get('/user/referral-stats'),
};

// ── Shop ──────────────────────────────────────────────────────
const shop = {
  getItems: () => instance.get('/shop/virtual-items'),
  getMyItems: () => instance.get('/shop/my-items'),
  purchase: (itemId) => instance.post(`/shop/purchase/${itemId}`),
  // Backend uses query-string for the virtual purchase endpoint;
  // historical name kept here so consumers don't churn.
  purchaseByQuery: (itemId) => instance.post(`/shop/virtual-items/purchase?item_id=${encodeURIComponent(itemId)}`),
  toggleItem: (userItemId) => instance.put(`/shop/my-items/${userItemId}/toggle`),
  // Set the per-instance variant (chosen emoji, color, …).
  setVariant: (userItemId, variant) => instance.put(`/shop/my-items/${userItemId}/variant`, { variant }),
  equip: (itemId) => instance.post(`/shop/equip/${itemId}`),
  unequip: (category) => instance.post(`/shop/unequip/${category}`),
};

// ── Admin ─────────────────────────────────────────────────────
const admin = {
  // Dashboard
  getDashboard: () => instance.get('/admin/dashboard'),

  // User-Anlegen ohne den Admin auszuloggen — /auth/register würde
  // das Auth-Cookie auf den frischen User setzen.
  createUser: (data) => instance.post('/admin/users', data),
  getPlatformRevenue: () => instance.get('/admin/platform-revenue'),
  getCreditPurchases: (params) => instance.get('/admin/credit-purchases', { params }),
  getPlatformNotifications: (params) => instance.get('/admin/notifications', { params }),
  getActivityLogs: (params) => instance.get('/admin/activity-logs', { params }),
  getSentryStatus: () => instance.get('/admin/sentry/status'),
  getSentryIssues: (params) => instance.get('/admin/sentry/issues', { params }),
  
  // Markets
  getMarkets: (params) => instance.get('/admin/markets', { params }),
  updateMarket: (id, data) => instance.put(`/admin/markets/${id}`, data),
  updateMarketStatus: (id, status) => instance.put(`/admin/markets/${id}/status?status=${status}`),
  // Admin-Override: Markt boosten/entboosten ohne Creator-Boost-Item.
  setMarketBoost: (id, boost, days = 7) => instance.put(`/admin/markets/${id}/boost`, { boost, days }),
  settleMarket: (id, outcomeId) => instance.post(`/admin/markets/${id}/settle`, { winning_outcome_id: outcomeId }),
  // Bulk settle: settlements = [{market_id, winning_outcome_id}, …].
  // auto_close=true (default) auto-closes still-open markets in the
  // batch so the admin doesn't have to do close-then-settle in two
  // round-trips.
  bulkSettle: (settlements, autoClose = true) =>
    instance.post('/admin/markets/bulk-settle', { settlements, auto_close: autoClose }),
  approveMarket: (id, body) => instance.put(`/admin/markets/${id}/approve`, body),
  rejectMarket: (id) => instance.put(`/admin/markets/${id}/reject`),
  deleteMarket: (id) => instance.delete(`/admin/markets/${id}`),
  getReviewQueue: () => instance.get('/admin/markets/review-queue'),
  getPendingMarkets: () => instance.get('/admin/pending-markets'),
  
  // Bets
  getAllBets: (params) => instance.get('/admin/bets', { params }),
  getActiveBets: (marketId) => instance.get(`/admin/bets/active/${marketId}`),
  getBettingStats: () => instance.get('/admin/betting-stats'),
  
  // Users
  getUsers: (params) => instance.get('/admin/users', { params }),
  getUser: (id) => instance.get(`/admin/users/${id}`),
  updateUser: (id, data) => instance.put(`/admin/users/${id}`, data),
  updateUserCredits: (userId, data) => instance.post(`/admin/users/${userId}/credits`, data),
  resetUserPassword: (userId, data) => instance.post(`/admin/users/${userId}/reset-password`, data),
  getUserAnalytics: () => instance.get('/admin/users/analytics'),
  approveCreator: (id) => instance.put(`/admin/users/${id}/approve-creator`),
  getCreatorApplications: () => instance.get('/admin/creator-applications'),
  
  // Stats
  getStats: () => instance.get('/admin/stats'),
  getTransactions: (params) => instance.get('/admin/transactions', { params }),
  getMarketPayouts: (marketId) => instance.get(`/admin/markets/${marketId}/payouts`),
  getWinLossAnalytics: (params) => instance.get('/admin/analytics/win-loss', { params }),
  getSettlementHistory: (params) => instance.get('/admin/settlements', { params }),
  
  // Fees
  getFeeSettings: () => instance.get('/admin/fee-settings'),
  updateFeeSettings: (data) => instance.put('/admin/fee-settings', data),
  getCommissionSettings: () => instance.get('/admin/commission-settings'),
  updateCommissionSettings: (data) => instance.put('/admin/commission-settings', data),
  getReferralSettings: () => instance.get('/admin/referral-settings'),
  updateReferralSettings: (data) => instance.put('/admin/referral-settings', data),
  
  // Charts
  getBettingTrends: (days) => instance.get(`/admin/chart-data/betting-trends?days=${days || 30}`),
  getCategoryDistribution: () => instance.get('/admin/chart-data/category-distribution'),
  getTopMarkets: (limit) => instance.get(`/admin/chart-data/top-markets?limit=${limit || 8}`),
  getUserGrowth: (days) => instance.get(`/admin/chart-data/user-growth?days=${days || 30}`),
  
  // Credit Packages
  getCreditPackages: () => instance.get('/admin/credit-packages'),
  createCreditPackage: (data) => instance.post('/admin/credit-packages', data),
  updateCreditPackage: (id, data) => instance.put(`/admin/credit-packages/${id}`, data),
  deleteCreditPackage: (id) => instance.delete(`/admin/credit-packages/${id}`),
  reorderCreditPackage: (id, data) => instance.put(`/admin/credit-packages/${id}/reorder`, data),
  
  // Civic
  getCivicMarkets: (params) => instance.get('/admin/civic/markets', { params }),
  getCivicMarket: (id) => instance.get(`/admin/civic/markets/${id}`),
  updateCivicMarket: (id, data) => instance.put(`/admin/civic/markets/${id}`, data),
  deleteCivicMarket: (id) => instance.delete(`/admin/civic/markets/${id}`),
  
  // Categories
  getCategories: () => instance.get('/admin/categories'),
  createCategory: (data) => instance.post('/admin/categories', data),
  updateCategory: (id, data) => instance.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => instance.delete(`/admin/categories/${id}`),
  
  // Shop
  getShopItems: () => instance.get('/admin/shop/items'),
  createShopItem: (data) => instance.post('/admin/shop/items', data),
  updateShopItem: (id, data) => instance.put(`/admin/shop/items/${id}`, data),
  deleteShopItem: (id) => instance.delete(`/admin/shop/items/${id}`),
  // Virtual shop items (cosmetics) — these are the entries users
  // actually see in the user-facing Shop page. Backed by
  // /admin/shop/virtual-items.
  getVirtualShopItems: () => instance.get('/admin/shop/virtual-items'),
  createVirtualShopItem: (data) => instance.post('/admin/shop/virtual-items', data),
  updateVirtualShopItem: (id, data) => instance.put(`/admin/shop/virtual-items/${id}`, data),
  deleteVirtualShopItem: (id) => instance.delete(`/admin/shop/virtual-items/${id}`),
  
  // Charities
  getCharities: () => instance.get('/admin/charities'),
  
  // Outcomes
  getOutcomesBulk: (params) => instance.get('/outcomes/bulk', { params }),
  createOutcome: (data) => instance.post('/outcomes', data),
  
  // User management extras
  deleteUser: (id) => instance.delete(`/admin/users/${id}`),
  resetUserPassword: (id, data, config) => instance.post(`/admin/users/${id}/reset-password`, data, config),
  updateCreatorApplication: (id, data) => instance.put(`/admin/creator-applications/${id}`, data),
  
  // Analytics
  getFeeAnalytics: (days) => instance.get(`/admin/fee-analytics?days=${days || 30}`),
  getWinLossAnalytics: (days) => instance.get(`/admin/win-loss-analytics?days=${days || 30}`),
};

// ── Creator ───────────────────────────────────────────────────
// Endpoints, die CreatorDashboard tatsächlich aufruft. Vorher waren
// hier nur Stubs für nicht existierende Backend-Routen — getMarkets()
// und getAnalyticsOverview() fehlten, was zu TypeError beim Öffnen
// von /creator führte.
const creator = {
  getMarkets: () => instance.get('/creator/my-markets'),
  createMarket: (data) => instance.post('/creator/markets', data),
  updateMarket: (id, data) => instance.put(`/creator/markets/${id}`, data),
  getAnalyticsOverview: () => instance.get('/creator/analytics-overview'),
  getMarketAnalytics: (marketId) => instance.get(`/creator/market-analytics/${marketId}`),
  getCreditsBreakdown: () => instance.get('/creator/credits-breakdown'),
  getBoosts: () => instance.get('/creator/boosts'),
  boostMarket: (marketId) => instance.post(`/creator/markets/${marketId}/boost`),
  apply: (data) => instance.post('/creator/apply', data),
  // Öffentlich (kein Auth) — für die „Creator werden"-Pitch-Seite.
  getCommissionRates: () => instance.get('/commission-rates'),
};

// ── Push Notifications ────────────────────────────────────────
const push = {
  subscribe: (data) => instance.post('/push-subscription', data),
};

// ── Media (Bild-Upload) ───────────────────────────────────────
const media = {
  // Lädt eine Bilddatei hoch (Backend konvertiert zu WebP, inkl. HEIC)
  // und gibt die ABSOLUTE Bild-URL zurück (image_url muss absolut sein,
  // damit <img> von jeder Origin lädt). Content-Type/boundary setzt der
  // Browser für FormData selbst — daher KEIN manueller Header.
  upload: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await instance.post('/media/upload', fd);
    return `${API_URL}${res.data.url}`;
  },
};

const api = {
  auth,
  markets,
  civic,
  bets,
  outcomes,
  credits,
  profile,
  friends,
  notifications,
  leaderboard,
  referrals,
  shop,
  admin,
  creator,
  push,
  media,
};

export default api;
